import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Unicon from '@/components/General/Unicon';

const getScheduleBounds = () => {
    const now = new Date();
    const minimumDate = new Date(now.getTime() + 15 * 60 * 1000);
    minimumDate.setMinutes(Math.ceil(minimumDate.getMinutes() / 15) * 15, 0, 0);
    const maximumDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    const defaultScheduledFor = new Date(now.getTime() + 60 * 60 * 1000);
    defaultScheduledFor.setMinutes(Math.round(defaultScheduledFor.getMinutes() / 15) * 15, 0, 0);
    return { minimumDate, maximumDate, defaultScheduledFor };
};

const formatScheduledFor = (scheduledFor) => {
    const date = scheduledFor ? new Date(scheduledFor) : null;
    if (!date || isNaN(date.getTime())) return null;
    return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

const getScheduledDate = (scheduleMode, scheduledFor) => {
    if (scheduleMode !== 'later' || !scheduledFor) return null;
    const scheduledDate = new Date(scheduledFor);
    return !isNaN(scheduledDate.getTime()) && scheduledDate.getTime() > Date.now() ? scheduledDate : null;
};

const ScheduleModeMenu = ({ scheduleMode = 'now', scheduledFor = null, onScheduleChange, nowLabel = 'Post Now', laterLabel = 'Post Later', colorVariant = 'connect', testID = 'schedule' }) => {
    const isLater = scheduleMode === 'later';
    const { defaultScheduledFor } = useMemo(() => getScheduleBounds(), []);
    const scheduledDisplay = isLater ? formatScheduledFor(scheduledFor) : null;
    const options = [
        {
            name: nowLabel,
            icon: dropdownIcons.create,
            selected: !isLater,
            onSelect: () => onScheduleChange({ scheduleMode: 'now', scheduledFor: null }),
        },
        {
            name: laterLabel,
            icon: { web: 'time-outline', ios: 'clock', android: 'schedule' },
            selected: isLater,
            onSelect: () => onScheduleChange({ scheduleMode: 'later', scheduledFor: scheduledFor || defaultScheduledFor }),
        },
    ];

    return (
        <DropdownMenu
            testID={`${testID}-mode`}
            align="end"
            options={options}
            triggerComponent={
                <View style={styles.scheduleChip(isLater, colorVariant)}>
                    <Unicon name={isLater ? 'time-outline' : 'flash-outline'} size={13} colorVariant={isLater ? colorVariant : 'textSecondary'} />
                    <Text style={styles.scheduleChipText(isLater, colorVariant)}>{isLater ? (scheduledDisplay || laterLabel) : nowLabel}</Text>
                    <Unicon name="chevron-down" size={12} colorVariant={isLater ? colorVariant : 'textSecondary'} />
                </View>
            }
        />
    );
};

const ScheduleDatePicker = ({ scheduledFor, onScheduleChange, pickerTitle = 'Schedule For', testID = 'schedule' }) => {
    const { minimumDate, maximumDate, defaultScheduledFor } = useMemo(() => getScheduleBounds(), []);
    return (
        <View style={styles.pickerRow} testID={`${testID}-picker`}>
            <DatePickerInput
                title={pickerTitle}
                mode="datetime"
                value={scheduledFor ? new Date(scheduledFor) : defaultScheduledFor}
                onUpdate={(date) => onScheduleChange({ scheduledFor: date })}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                minuteInterval={15}
                hideClearIcon
            />
        </View>
    );
};

const SchedulePicker = ({ style, menuStyle, ...props }) => {
    const isLater = props.scheduleMode === 'later';
    return (
        <View style={[styles.container, style]}>
            <View style={[styles.menuRow, menuStyle]}>
                <ScheduleModeMenu {...props} />
            </View>
            {isLater && <ScheduleDatePicker {...props} />}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        width: '100%',
    },
    menuRow: {
        alignItems: 'flex-end',
    },
    scheduleChip: (isLater, colorVariant) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 100,
        backgroundColor: isLater ? colors[colorVariant + 'Container'] : colors.surfaceVariant,
    }),
    scheduleChipText: (isLater, colorVariant) => ({
        fontSize: 12,
        fontFamily: fonts.bold,
        color: isLater ? colors[colorVariant] : colors.textSecondary,
    }),
    pickerRow: {
        marginTop: 4,
        marginBottom: 6,
    },
}));

export { ScheduleModeMenu, ScheduleDatePicker, getScheduleBounds, formatScheduledFor, getScheduledDate };
export default SchedulePicker;
